import classNames from "classnames";
import * as React from "react";
import Icon from "../../components/atoms/Icon";
import { DrillProgressBarGroup } from "../../components/features/DrillProgressBarGroup";
import {
  applyAllowStudiedAnswer,
  calcTotalCount,
} from "../../domains/DrillProgress";
import {
  type DrillContent,
  type DrillContentItem,
  isFullyNonAnswerableContent,
} from "./types";

type ContentPanelProps = {
  contents: DrillContent[];
  selectedContentId: string;
  onSelectContent: (id: string) => void;
  selectedItemPublicId: string | undefined;
  onSelectItem: (publicId: string) => void;
  sectionName: string;
  allowStudiedAnswer: boolean;
};

export const ContentPanel = ({
  contents,
  selectedContentId,
  onSelectContent,
  selectedItemPublicId,
  onSelectItem,
  sectionName,
  allowStudiedAnswer,
}: ContentPanelProps) => {
  return (
    <div className="rounded-md border border-solid border-gray-400 bg-white py-4">
      {/* セクションタイトル */}
      <div className="px-4 pb-4">
        <div className="text-[18px] font-medium leading-[1.3] text-black">
          {sectionName}
        </div>
      </div>

      {/* コンテンツ一覧 */}
      {contents.map((content, index) => {
        const isContentSelected = content.id === selectedContentId;
        const { progress } = content;
        const answeredCount =
          progress.studyingCount +
          progress.establishedCount +
          progress.perfectCount +
          (progress.studiedCount ?? 0);
        // DISP-11: アイテム選択 UI は複数アイテムのときだけ
        const isItemSelectionEnabled = content.items.length > 1;
        const isSingleItem = content.items.length === 1;

        // このコンテンツ内で選択中アイテムが何番目か（このコンテンツが非選択 or 該当なしなら -1）
        const selectedItemIndex =
          isContentSelected && selectedItemPublicId
            ? content.items.findIndex(
                (i) => i.publicId === selectedItemPublicId,
              )
            : -1;

        // 1 つ目アイテムが「ヘッダグループ」に含まれて青枠の対象になる条件:
        //   - 単一アイテムコンテンツ
        //   - 複数アイテムで 1 つ目アイテムが選択中
        // それ以外（2 つ目以降選択 / コンテンツ非選択）はヘッダグループに青枠は当たらない
        const isHeaderGroupHighlighted =
          isContentSelected && (isSingleItem || selectedItemIndex === 0);

        // DISP-13: コンテンツ配下のアイテムが全て回答を持たない種別なら、
        // 回答済 N/M と定着度バーを非表示にする (選択状態に依存しない構造的判定)
        const isProgressDisplayHidden = isFullyNonAnswerableContent(content);

        const handleSelectItem = (itemPublicId: string) => {
          if (!isContentSelected) {
            onSelectContent(content.id);
          }
          onSelectItem(itemPublicId);
        };

        // コンテンツヘッダー（タイトル＋進捗）クリックで、そのコンテンツの先頭アイテムを選ぶ
        const handleSelectContentHeader = () => {
          const firstItemPublicId = content.items[0]?.publicId;
          if (!isContentSelected) {
            onSelectContent(content.id);
          }
          if (firstItemPublicId) {
            onSelectItem(firstItemPublicId);
          }
        };

        const [firstItem, ...restItems] = content.items;

        return (
          <React.Fragment key={content.id}>
            {index > 0 && (
              <div className="py-4">
                <div className="h-px w-full bg-gray-400" />
              </div>
            )}
            <div className="flex flex-col gap-4">
              {/* ヘッダグループ: コンテンツヘッダ + 1 つ目アイテム
                  単一アイテム時 / 複数アイテムで 1 つ目選択時 はこの塊全体に青枠 */}
              <div
                className={classNames(
                  "flex flex-col gap-4 rounded-md px-4 py-4",
                  isHeaderGroupHighlighted &&
                    "bg-blue-100 ring-2 ring-inset ring-blue-400",
                )}
              >
                {/* コンテンツ名 + 進捗 (クリックで先頭アイテム選択) */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={handleSelectContentHeader}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelectContentHeader();
                    }
                  }}
                  className="flex cursor-pointer flex-col gap-4"
                >
                  <div className="text-lg font-bold leading-[1.4] text-black">
                    {index + 1}. {content.name}
                  </div>

                  {!isProgressDisplayHidden && (
                    <div className="flex flex-col gap-2.5">
                      <div className="text-[13px] text-black">
                        回答済：{answeredCount}/
                        {content.registeredStudentsCount}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="shrink-0 text-[13px] font-medium text-black">
                          定着度
                        </span>
                        <div className="flex flex-1 overflow-hidden rounded-[7px]">
                          <DrillProgressBarGroup
                            totalCount={calcTotalCount(
                              applyAllowStudiedAnswer(
                                content.progress,
                                allowStudiedAnswer,
                              ),
                            )}
                            progress={content.progress}
                            allowStudiedAnswer={allowStudiedAnswer}
                            bold
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* 1 つ目アイテム (ヘッダグループの一部として常に直下に表示) */}
                {firstItem && (
                  <ContentItemRow
                    item={firstItem}
                    isSelectionEnabled={isItemSelectionEnabled}
                    isStandalone={false}
                    isStandaloneHighlighted={false}
                    onSelect={() => handleSelectItem(firstItem.publicId)}
                  />
                )}
              </div>

              {/* ヘッダグループ外のアイテム (複数アイテムの 2 つ目以降)
                  選択中ならアイテム単独で青枠、それ以外は素のレイアウト */}
              {restItems.map((item) => (
                <ContentItemRow
                  key={item.publicId}
                  item={item}
                  isSelectionEnabled={isItemSelectionEnabled}
                  isStandalone
                  isStandaloneHighlighted={
                    item.publicId === selectedItemPublicId
                  }
                  onSelect={() => handleSelectItem(item.publicId)}
                />
              ))}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const SECTION_PANEL_DESCRIPTION_LIMIT = 80;

const ContentItemRow = ({
  item,
  isSelectionEnabled,
  isStandalone,
  isStandaloneHighlighted,
  onSelect,
}: {
  item: DrillContentItem;
  isSelectionEnabled: boolean;
  isStandalone: boolean;
  isStandaloneHighlighted: boolean;
  onSelect: () => void;
}) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] =
    React.useState(false);

  const isLongDescription = !!(
    item.description &&
    item.description.length > SECTION_PANEL_DESCRIPTION_LIMIT
  );
  const displayDescription =
    !isDescriptionExpanded && isLongDescription
      ? `${item.description?.substring(0, SECTION_PANEL_DESCRIPTION_LIMIT) ?? ""}...`
      : (item.description ?? "");
  const shouldShowChoices = !isLongDescription || isDescriptionExpanded;

  const interactiveProps = isSelectionEnabled
    ? {
        role: "button" as const,
        tabIndex: 0,
        onClick: onSelect,
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect();
          }
        },
      }
    : {};

  return (
    <div
      {...interactiveProps}
      className={classNames(
        "flex flex-col gap-2.5 rounded-md",
        isStandalone && "px-4 py-4",
        isSelectionEnabled && "cursor-pointer",
        isStandaloneHighlighted &&
          "bg-blue-100 ring-2 ring-inset ring-blue-400",
      )}
    >
      {item.description && (
        <div className="text-lg leading-[1.4] text-black">
          {displayDescription}
        </div>
      )}

      {isLongDescription && (
        <button
          type="button"
          className="flex items-center gap-1.5 self-start border-0 bg-transparent p-0 text-[14px] font-bold leading-[14px] text-blue-400"
          onClick={(e) => {
            e.stopPropagation();
            setIsDescriptionExpanded(!isDescriptionExpanded);
          }}
        >
          <Icon
            name="icon-arrow-down"
            className={classNames(
              "h-6 w-6 fill-blue-400 transition-transform",
              { "rotate-180": isDescriptionExpanded },
            )}
          />
          すべて見る
        </button>
      )}

      {shouldShowChoices && <ItemChoices item={item} />}

      {item.imageUrl && <ItemMedia item={item} />}

      {item.answer != null && <ItemAnswer answer={item.answer} />}
    </div>
  );
};

const ItemChoices = ({ item }: { item: DrillContentItem }) => {
  if (
    item.itemType === "form_single_text" ||
    item.itemType === "form_understanding_text"
  ) {
    return null;
  }

  if (!item.choices?.length) {
    return null;
  }
  const choices = item.choices;

  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2 text-lg text-gray-900">
      {choices.map((choice, index) => (
        <span key={`${choice}-${index}`}>{choice}</span>
      ))}
    </div>
  );
};

const ItemMedia = ({ item }: { item: DrillContentItem }) => {
  const [isEnlarged, setIsEnlarged] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    if (!isEnlarged) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsEnlarged(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isEnlarged]);

  if (!item.imageUrl) {
    return null;
  }

  if (item.mediaType === "audio") {
    return <audio src={item.imageUrl} controls className="w-full" />;
  }

  if (item.mediaType === "pdf") {
    return (
      <a
        href={item.imageUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-sm font-bold text-blue-400"
      >
        <Icon
          name="icon-external-link"
          className="h-3 w-3 shrink-0 fill-blue-400"
        />
        {item.fileName ?? "PDFを表示"}
      </a>
    );
  }

  if (hasError) {
    return (
      <div className="flex items-center gap-2 rounded bg-gray-100 px-3 py-2 text-sm text-gray-600">
        <Icon name="icon-picture-img" className="h-5 w-5 fill-gray-400" />
        画像を読み込めませんでした
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className="group max-w-[70%] cursor-pointer border-0 bg-transparent p-0"
        onClick={(e) => {
          e.stopPropagation();
          setIsEnlarged(true);
        }}
      >
        <span className="relative inline-block">
          <img
            src={item.imageUrl}
            alt="コンテンツ画像"
            className="block h-auto max-w-full transition-opacity group-hover:opacity-50"
            onError={() => setHasError(true)}
          />
          <span className="absolute top-1 right-1">
            <ExpandIcon />
          </span>
        </span>
      </button>
      {isEnlarged && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-label="画像拡大表示"
          onClick={() => setIsEnlarged(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setIsEnlarged(false);
            }
          }}
          tabIndex={0}
        >
          <button
            type="button"
            className="absolute top-7 right-7 z-10 cursor-pointer border-none bg-transparent p-0 transition-opacity hover:opacity-50"
            onClick={(e) => {
              e.stopPropagation();
              setIsEnlarged(false);
            }}
            aria-label="閉じる"
          >
            <CloseIcon />
          </button>
          <div
            className="flex max-h-[90vh] max-w-[90vw] items-center justify-center"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <img
              src={item.imageUrl}
              alt="コンテンツ画像（拡大）"
              className="min-h-[50vh] min-w-[50vw] max-h-[90vh] max-w-[90vw] object-contain"
            />
          </div>
        </div>
      )}
    </>
  );
};

const ItemAnswer = ({ answer }: { answer: string }) => {
  return (
    <div className="flex items-center gap-3 flex-nowrap">
      <span className="shrink-0 rounded border border-solid border-gray-700 px-1.5 py-0.5 text-xs text-gray-900">
        正答
      </span>
      <span className="text-lg text-gray-900">{answer}</span>
    </div>
  );
};

const ExpandIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect
      x="0.5"
      y="0.5"
      width="23"
      height="23"
      rx="3.5"
      className="fill-white stroke-blue-400 transition-colors group-hover:fill-blue-400"
    />
    <path
      d="M20 20L15.3806 15.3806M15.3806 15.3806C16.6309 14.1304 17.3333 12.4347 17.3333 10.6666C17.3333 8.89853 16.6309 7.20285 15.3806 5.95261C14.1304 4.70237 12.4347 4 10.6666 4C8.89853 4 7.20285 4.70237 5.95261 5.95261C4.70237 7.20285 4 8.89853 4 10.6666C4 12.4347 4.70237 14.1304 5.95261 15.3806C7.20285 16.6309 8.89853 17.3333 10.6666 17.3333C12.4347 17.3333 14.1304 16.6309 15.3806 15.3806ZM10.6671 8.00052V13.3336M13.3336 10.6671H8.00052"
      strokeWidth="1.33328"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="stroke-blue-400 transition-colors group-hover:stroke-white"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect width="40" height="40" rx="20" className="fill-white" />
    <path
      d="M18.6557 20L13.2784 14.6227C12.9072 14.2515 12.9072 13.6496 13.2784 13.2784C13.6496 12.9072 14.2515 12.9072 14.6227 13.2784L20 18.6557L25.3773 13.2784C25.7485 12.9072 26.3504 12.9072 26.7216 13.2784C27.0928 13.6496 27.0928 14.2515 26.7216 14.6227L21.3443 20L26.7216 25.3773C27.0928 25.7485 27.0928 26.3504 26.7216 26.7216C26.3504 27.0928 25.7485 27.0928 25.3773 26.7216L20 21.3443L14.6227 26.7216C14.2515 27.0928 13.6496 27.0928 13.2784 26.7216C12.9072 26.3504 12.9072 25.7485 13.2784 25.3773L18.6557 20Z"
      className="fill-gray-700"
    />
  </svg>
);
